import React, { useState } from 'react';
import { api, endpoints } from '../../config/api';
import { Card, Button, Badge } from '../ui';
import { useT } from '../../i18n';
import './AddinDeploymentCard.css';

const messages = {
  title: { tr: 'Outlook Add-in Dağıtımı', en: 'Outlook Add-in Deployment' },
  manualStep: { tr: 'Manuel Adım', en: 'Manual Step' },
  desc: {
    tr: () => <>İmzaların Outlook'ta gönderim anında otomatik eklenmesi için organizasyonunuzun<strong> Global Admin</strong> veya <strong>Exchange Admin</strong> yetkisine sahip bir yöneticisinin, Microsoft 365 Admin Center üzerinden eklentimizi organizasyon geneline dağıtması gerekir. Adımları tamamlamak yaklaşık 5 dakika sürer — bu tek seferlik bir işlemdir (eklentinin kullanıcılarda görünmesi ayrıca Microsoft'un dokümanına göre 24-72 saati bulabilir).</>,
    en: () => <>For signatures to be added automatically in Outlook, a <strong>Global Admin</strong> or <strong>Exchange Admin</strong> in your organization needs to deploy our add-in organization-wide from the Microsoft 365 admin center. It takes about 5 minutes and only has to be done once (according to Microsoft’s documentation, the add-in can take 24–72 hours to appear for users).</>,
  },
  hideSteps: { tr: 'Adımları Gizle', en: 'Hide Steps' },
  showSteps: { tr: 'Nasıl Dağıtılır? Adımları Göster', en: 'How to Deploy? Show Steps' },
  step1: {
    tr: ({ url }) => <><a href={url} target="_blank" rel="noopener noreferrer">Microsoft 365 Admin Center &gt; Tümleşik Uygulamalar</a>{' '}sayfasını açın (Global Admin veya Exchange Admin ile giriş yapmalısınız).</>,
    en: ({ url }) => <>Open{' '}<a href={url} target="_blank" rel="noopener noreferrer">Microsoft 365 admin center &gt; Integrated apps</a>{' '}(sign in as a Global Admin or Exchange Admin).</>,
  },
  step2: {
    tr: () => <><strong>+ Özel uygulamalar</strong> &gt; <strong>Özel uygulama yükle</strong> seçin.</>,
    en: () => <>Choose <strong>Upload custom apps</strong>.</>,
  },
  step3: {
    tr: () => <><strong>Manifest dosyası sağla</strong> &gt; <strong>URL'den yükle</strong> seçeneğini seçip aşağıdaki adresi yapıştırın:</>,
    en: () => <>Select <strong>Provide link to manifest file</strong> and paste the address below:</>,
  },
  copied: { tr: 'Kopyalandı ✓', en: 'Copied ✓' },
  copy: { tr: 'Kopyala', en: 'Copy' },
  step4: {
    tr: () => <><strong>Doğrula</strong> butonuna tıklayın, ardından uygulama adını onaylayıp <strong>İleri</strong> deyin.</>,
    en: () => <>Click <strong>Validate</strong>, then confirm the app name and click <strong>Next</strong>.</>,
  },
  step5: {
    tr: () => <>Kullanıcı seçiminde <strong>Tüm kuruluş</strong> seçeneğini işaretleyin (varsayılan durum: Etkin).</>,
    en: () => <>Under users, select <strong>Entire organization</strong> (default state: On).</>,
  },
  step6: {
    tr: () => <><strong>Dağıt</strong> butonuna tıklayın. Microsoft'un dokümanına göre eklentinin kullanıcıların Outlook'unda görünmesi <strong>24-72 saati</strong> bulabilir.</>,
    en: () => <>Click <strong>Deploy</strong>. According to Microsoft’s documentation, the add-in can take <strong>24–72 hours</strong> to appear in users’ Outlook.</>,
  },
  done: { tr: 'Bu Adımları Tamamladım', en: 'I’ve Completed These Steps' },
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const MANIFEST_URL = `${API_BASE_URL}/outlook-addin/manifest-validated.xml`;
const ADMIN_CENTER_URL = 'https://admin.microsoft.com/Adminportal/Home#/Settings/IntegratedApps';

// `onDeployed` is optional — pages that track completion status (e.g. to
// hide this card once done) pass a refetch callback; standalone usage just
// omits it.
export const AddinDeploymentCard = ({ onDeployed }) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [marking, setMarking] = useState(false);
  const { t } = useT(messages);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(MANIFEST_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable — the URL is still selectable/copyable by hand.
    }
  };

  const handleMarkDeployed = async () => {
    setMarking(true);
    try {
      await api.post(endpoints.organizations.addinMarkDeployed);
      if (onDeployed) await onDeployed();
    } catch {
      // Best-effort — the card just stays visible if this fails, no harm done.
    } finally {
      setMarking(false);
    }
  };

  return (
    <Card className="addin-deployment-card">
      <div className="addin-deployment-header">
        <div className="addin-deployment-title">
          <span className="addin-deployment-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="M3 7l9 6 9-6" />
            </svg>
          </span>
          <h3>{t('title')}</h3>
        </div>
        <Badge variant="info">{t('manualStep')}</Badge>
      </div>

      <p className="addin-deployment-desc">{t('desc')}</p>

      <Button variant="secondary" onClick={() => setExpanded(v => !v)}>
        {expanded ? t('hideSteps') : t('showSteps')}
      </Button>

      {expanded && (
        <div className="addin-deployment-steps">
          <ol>
            <li>{t('step1', { url: ADMIN_CENTER_URL })}</li>
            <li>{t('step2')}</li>
            <li>{t('step3')}</li>
          </ol>

          <div className="addin-deployment-manifest-box">
            <code>{MANIFEST_URL}</code>
            <Button onClick={handleCopy}>{copied ? t('copied') : t('copy')}</Button>
          </div>

          <ol start="4">
            <li>{t('step4')}</li>
            <li>{t('step5')}</li>
            <li>{t('step6')}</li>
          </ol>

          <Button onClick={handleMarkDeployed} disabled={marking}>
            {marking ? t('saving') : t('done')}
          </Button>
        </div>
      )}
    </Card>
  );
};

export default AddinDeploymentCard;
